"use client"

import { useEffect, useState } from "react"
import { usePathname } from "next/navigation"

export const COOKIE_CONSENT_NAME = "metka_cookie_consent"
const STORAGE_KEY = "metka_cookie_consent"
const ACCEPTED_VALUE = "accepted"
export const COOKIE_CONSENT_EVENT = "metka:cookie-consent-accepted"

export function hasCookieConsent(): boolean {
  if (typeof window === "undefined") return false
  try {
    if (window.localStorage.getItem(STORAGE_KEY) === ACCEPTED_VALUE) return true
  } catch {
    // ignore
  }
  // Fallback: cookie (для случая когда localStorage отключён)
  return document.cookie
    .split(";")
    .some((c) => c.trim().startsWith(`${COOKIE_CONSENT_NAME}=${ACCEPTED_VALUE}`))
}

export function CookieConsentBanner() {
  const pathname = usePathname()
  const [visible, setVisible] = useState(false)

  // Баннер только на публичной части — в ЛК и админке не показываем
  const isPublicPage = !pathname.startsWith("/lk") && !pathname.startsWith("/admin")

  useEffect(() => {
    if (!isPublicPage) {
      setVisible(false)
      return
    }
    setVisible(!hasCookieConsent())
  }, [isPublicPage, pathname])

  if (!visible) return null

  const accept = () => {
    try {
      window.localStorage.setItem(STORAGE_KEY, ACCEPTED_VALUE)
    } catch {
      // private mode / storage отключён — fallback только cookie
    }
    // Cookie нужен для server-side gate noscript-пикселя в layout.tsx.
    // 1 год; SameSite=Lax — достаточно для own-site трекера.
    const oneYear = 60 * 60 * 24 * 365
    document.cookie = `${COOKIE_CONSENT_NAME}=${ACCEPTED_VALUE}; path=/; max-age=${oneYear}; SameSite=Lax`
    window.dispatchEvent(new CustomEvent(COOKIE_CONSENT_EVENT))
    setVisible(false)
  }

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Согласие на использование cookie"
      className="fixed bottom-0 inset-x-0 z-[60] bg-[#0b1020] text-[#f4f4f5] border-t border-[rgba(244,244,245,0.10)] shadow-[0_-8px_24px_rgba(11,16,32,0.25)]"
    >
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 py-4 sm:py-5 flex flex-col sm:flex-row sm:items-center gap-4">
        <p className="text-[13px] leading-[1.55] flex-1">
          Мы используем файлы cookie и сервис веб-аналитики Яндекс.Метрика для обеспечения работы сайта,
          анализа посещаемости и улучшения пользовательского опыта. Нажимая «Принять» или продолжая
          пользоваться сайтом, вы соглашаетесь на обработку данных с использованием cookie.
          Подробнее об обработке данных вы можете узнать в{" "}
          <a
            href="/privacy"
            target="_blank"
            rel="noreferrer"
            className="underline underline-offset-[3px] font-medium hover:text-white"
          >
            Политике конфиденциальности
          </a>
          .
        </p>
        <button
          type="button"
          onClick={accept}
          className="shrink-0 inline-flex items-center justify-center bg-[#f4f4f5] text-[#0b1020] text-[13px] font-semibold tracking-[0.015em] px-6 py-3 rounded-[4px] hover:opacity-92 transition-opacity"
        >
          Принять
        </button>
      </div>
    </div>
  )
}
